// src/components/admin/teamTaskbox/widgets/OverdueTasksWidget.js
import React from 'react';
import { AlertTriangle, Calendar, Users, Flag } from 'lucide-react';

const OverdueTasksWidget = ({ tasks, onTaskClick }) => {
    const overdueTasks = tasks
        .filter(task => task.overdue_days > 0)
        .sort((a, b) => b.overdue_days - a.overdue_days)
        .slice(0, 5); // Top 5 most overdue

    // Helper function to calculate assigned employees count
    const getAssignedEmployeesCount = (task) => {
        return task.assigned_employee_ids ?
            (Array.isArray(task.assigned_employee_ids) ? task.assigned_employee_ids.length :
             typeof task.assigned_employee_ids === 'string' ? task.assigned_employee_ids.split(',').filter(id => id).length : 0)
            : (task.assigned_employees_count || 0);
    };

    const priorityColors = {
        immediate: 'text-red-600 dark:text-red-400',
        urgent: 'text-orange-600 dark:text-orange-400',
        next_in_line: 'text-yellow-600 dark:text-yellow-400',
        scheduled: 'text-green-600 dark:text-green-400',
        deferred: 'text-gray-600 dark:text-gray-400'
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No deadline';
        // Extract YYYY-MM-DD from ISO string
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        // Create date in local timezone without UTC conversion
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getSeverityColor = (days) => {
        if (days >= 7) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300';
        if (days >= 3) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300';
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300';
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Overdue Tasks
                </h3>
                <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">
                    {overdueTasks.length} tasks
                </span>
            </div>

            {overdueTasks.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                        <AlertTriangle className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">No overdue tasks!</p>
                    <p className="text-sm text-gray-500 mt-1">Great job keeping up with deadlines</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {overdueTasks.map(task => (
                        <div
                            key={task.id}
                            onClick={() => onTaskClick(task)}
                            className="p-4 border-l-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow bg-gray-50 dark:bg-gray-700"
                            style={{ borderLeftColor: task.overdue_days >= 7 ? '#dc2626' : task.overdue_days >= 3 ? '#f59e0b' : '#fbbf24' }}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-start gap-2 flex-1">
                                    <Flag className={`w-4 h-4 ${priorityColors[task.priority]} flex-shrink-0 mt-0.5`} fill="currentColor" />
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-gray-900 dark:text-white line-clamp-2 mb-1">
                                            {task.title}
                                        </h4>
                                        {task.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                                                {task.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-bold whitespace-nowrap ml-2 ${getSeverityColor(task.overdue_days)}`}>
                                    {task.overdue_days}d overdue
                                </span>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mt-3">
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>{formatDate(task.deadline)}</span>
                                </div>
                                {getAssignedEmployeesCount(task) > 0 && (
                                    <div className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        <span>{getAssignedEmployeesCount(task)} assigned</span>
                                    </div>
                                )}
                                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs">
                                    {task.status.replace('_', ' ')}
                                </span>
                            </div>
                        </div>
                    ))}

                    {tasks.filter(t => t.overdue_days > 0).length > 5 && (
                        <div className="text-center pt-2">
                            <p className="text-sm text-gray-500">
                                +{tasks.filter(t => t.overdue_days > 0).length - 5} more overdue tasks
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OverdueTasksWidget;
